import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import IntegrationProviderIcon from '../IntegrationProviderIcon';

const REASONS = {
  PIPEDRIVE_NOTE_CREATE_UNCERTAIN: 'The lead was saved, but the summary note could not be confirmed. Check its notes in Pipedrive.',
  PIPEDRIVE_CONTACT_PHONE_INVALID: 'The callback number needs a country code.',
  PIPEDRIVE_CONTACT_EMAIL_INVALID: 'The contact email address needs correction.',
  PIPEDRIVE_CONTACT_MATCH_AMBIGUOUS: 'Multiple contacts match. Review them in Pipedrive.',
  PIPEDRIVE_CONTACT_DETAILS_CONFLICT: 'The contact details conflict with an existing contact. Review them in Pipedrive.',
  PIPEDRIVE_CONTACT_CREATE_UNCERTAIN: 'Contact creation could not be confirmed. Check Pipedrive before trying again.',
  CRM_CONNECTION_UNAVAILABLE: 'Reconnect the CRM account before retrying.',
};

export default function WorkflowAttention({ backendHost, authConfig, workflows }) {
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [notice, setNotice] = useState('');
  const load = useCallback(async () => {
    const response = await axios.get(`${backendHost}/v2/integrations/sync-jobs?attention=true&limit=100`, authConfig);
    setJobs(response.data.jobs || []);
    setTotal(response.data.total ?? response.data.jobs?.length ?? 0);
  }, [backendHost, authConfig]);

  useEffect(() => {
    let active = true;
    const refresh = async () => {
      try {
        const response = await axios.get(`${backendHost}/v2/integrations/sync-jobs?attention=true&limit=100`, authConfig);
        if (active) {
          setJobs(response.data.jobs || []);
          setTotal(response.data.total ?? response.data.jobs?.length ?? 0);
          setError('');
        }
      } catch {
        if (active) setError('Workflow results could not be loaded.');
      }
    };
    refresh();
    const onFocus = () => refresh();
    const timer = window.setInterval(() => {
      if (!document.hidden) refresh();
    }, 30000);
    window.addEventListener('focus', onFocus);
    return () => {
      active = false;
      window.clearInterval(timer);
      window.removeEventListener('focus', onFocus);
    };
  }, [backendHost, authConfig]);

  async function dismissAll() {
    setBusy('dismiss-all');
    setError('');
    setNotice('');
    try {
      const response = await axios.post(`${backendHost}/v2/integrations/sync-jobs/dismiss-all`, {}, authConfig);
      const count = response.data.dismissedCount;
      setNotice(`${count} CRM sync ${count === 1 ? 'attempt' : 'attempts'} dismissed. History is retained for support.`);
      try { await load(); } catch { setError('Dismissal completed, but the list could not be updated.'); }
    } catch (failure) {
      setError(failure.response?.data?.error?.message || 'CRM sync attempts could not be dismissed. Please try again.');
    } finally { setBusy(''); }
  }

  async function retry(job) {
    setBusy(job.id);
    setError('');
    setNotice('');
    try {
      const response = await axios.post(`${backendHost}/v2/integrations/sync-jobs/${job.id}/retry`, {}, authConfig);
      const succeeded = response.data.job?.status === 'succeeded';
      setNotice(succeeded ? 'The CRM record was saved.' : 'This run still needs attention. Review the details below.');
      await load();
    } catch (failure) {
      setError(failure.response?.data?.error?.message || 'The retry could not be completed. Refresh to check its status.');
    } finally { setBusy(''); }
  }

  async function dismiss(job) {
    setBusy(`dismiss:${job.id}`);
    setError('');
    try {
      await axios.post(`${backendHost}/v2/integrations/sync-jobs/${job.id}/dismiss`, {}, authConfig);
      setNotice('Run dismissed. Its history is retained for support.');
      await load();
    } catch (failure) {
      setError(failure.response?.data?.error?.message || 'The run could not be dismissed. Refresh and try again.');
    } finally { setBusy(''); }
  }

  if (!total && !error) return null;
  return <details className="workflow-attention">
    <summary>
      <span className="workflow-attention-symbol" aria-hidden="true">!</span>
      <span className="workflow-attention-title">{error && !total ? 'Workflow results unavailable' : 'Needs attention'}</span>
      <span className="workflow-attention-count">{total}</span>
      <span className="workflow-attention-chevron" aria-hidden="true">⌄</span>
    </summary>
    <div className="workflow-attention-body">
      <div className="workflow-attention-toolbar"><span>Review unsuccessful CRM sync attempts or dismiss ones you no longer need.</span><button type="button" className="workflow-attention-button" onClick={dismissAll} disabled={Boolean(busy) || !total} title="Dismiss all unsuccessful CRM sync attempts and retain their history">{busy === 'dismiss-all' ? 'Dismissing…' : 'Dismiss all'}</button></div>
      {error ? <p role="alert">{error}</p> : null}
      {notice ? <p role="status">{notice}</p> : null}
      {total > jobs.length ? <p>Showing the latest {jobs.length} of {total} runs.</p> : null}
      {jobs.map(job => {
        const workflowId = job.result?.workflowSelection?.workflowId;
        const workflow = workflows.find(item => item.id === workflowId);
        const contact = job.result?.relatedRecords?.contact;
        const reason = REASONS[job.error] || (job.error && !/^[A-Z0-9_]+$/.test(job.error) ? job.error : 'The CRM record could not be saved. Review the workflow and CRM connection.');
        return <article key={job.id} className="workflow-attention-run">
          <div className="workflow-attention-run-heading">
            <IntegrationProviderIcon provider={job.provider} className="workflow-attention-provider" />
            <div><strong>{workflow?.name || `${job.provider === 'salesforce' ? 'Salesforce' : job.provider === 'pipedrive' ? 'Pipedrive' : job.provider} workflow`}</strong>
            <time dateTime={job.dateCreated}>{new Date(job.dateCreated).toLocaleString()}</time></div>
            <span className="workflow-attention-status">{job.status === 'needs_review' ? 'Review needed' : 'Not completed'}</span>
          </div>
          <p>{reason}</p>
          {job.externalRecordId ? <p>The CRM record was saved. A follow-up step needs attention; retrying will reuse the saved record.</p>
            : contact?.id ? <p>The contact was saved. Completion of the workflow record could not be confirmed. Retrying will reuse that contact.</p> : null}
          <div className="workflow-attention-actions">
            {job.callId ? <Link className="workflow-attention-button" to={`/calls?callId=${encodeURIComponent(job.callId)}`}>View call</Link> : null}
            {job.canRetry ? <button type="button" className="workflow-attention-button primary" disabled={Boolean(busy)} onClick={() => retry(job)}>{busy === job.id ? 'Retrying…' : 'Retry'}</button> : null}
            <button type="button" className="workflow-attention-button dismiss" disabled={Boolean(busy)} onClick={() => dismiss(job)} title="Remove from this list and retain the run history">{busy === `dismiss:${job.id}` ? 'Dismissing…' : 'Dismiss'}</button>
          </div>
          {!job.canRetry ? <p className="workflow-attention-blocked">{job.retryBlockedReason || 'This run requires manual review of the CRM and collected details.'}</p> : null}
        </article>;
      })}
    </div>
  </details>;
}
